import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, TrendingUp, TrendingDown, LineChart as LineIcon, Activity } from "lucide-react";
import { LineChart, Line, YAxis, ReferenceLine, ResponsiveContainer, Tooltip } from "recharts";


/** Dashboard watchlist: shows HOD/LOD/current + spark chart per symbol. */
export function WatchlistWidget() {
  const [symbols, setSymbols] = useState([]);
  const [quotes, setQuotes] = useState({});       // symbol -> quote payload
  const [sparks, setSparks] = useState({});       // symbol -> intraday bars
  const [adding, setAdding] = useState(false);
  const [newSym, setNewSym] = useState("");
  const [loading, setLoading] = useState(true);

  const loadWatchlist = useCallback(async () => {
    try {
      const { data } = await api.get("/market/watchlist");
      setSymbols(data.symbols || []);
    } catch (err) {
      console.debug("[watchlist] load failed", err);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadWatchlist(); }, [loadWatchlist]);

  const loadQuotes = useCallback(async (syms) => {
    for (const s of syms) {
      try {
        const q = await api.get(`/market/quote/${s}`);
        setQuotes((prev) => ({ ...prev, [s]: q.data }));
      } catch (err) {
        setQuotes((prev) => ({ ...prev, [s]: { error: err?.response?.data?.detail || "No data" } }));
      }
      try {
        const i = await api.get(`/market/intraday/${s}`);
        setSparks((prev) => ({ ...prev, [s]: i.data.bars || [] }));
      } catch (err) {
        console.debug("[watchlist] intraday miss", s, err?.response?.status);
      }
    }
  }, []);
  useEffect(() => { if (symbols.length) loadQuotes(symbols); }, [symbols, loadQuotes]);

  const add = async () => {
    const clean = newSym.trim().toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (clean.length < 2) { toast.error("Enter a symbol"); return; }
    try {
      const { data } = await api.post("/market/watchlist", { symbol: clean });
      setSymbols(data.symbols || []);
      setNewSym(""); setAdding(false);
      toast.success(`Watching ${clean}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add");
    }
  };

  const remove = async (s) => {
    try {
      const { data } = await api.delete(`/market/watchlist/${s}`);
      setSymbols(data.symbols || []);
      setQuotes((prev) => { const c = { ...prev }; delete c[s]; return c; });
      setSparks((prev) => { const c = { ...prev }; delete c[s]; return c; });
    } catch (e) { toast.error("Failed to remove"); }
  };

  if (loading) return null;

  return (
    <section className="card-sharp p-5 mb-6" data-testid="watchlist-widget">
      <div className="flex items-center gap-2 mb-4">
        <Activity size={14} className="text-[var(--accent)]"/>
        <h3 className="font-display text-lg text-white flex-1">Live watchlist</h3>
        <span className="chip">HOD · LOD · breakout alerts</span>
        <button data-testid="watchlist-add-btn"
          onClick={() => setAdding(!adding)}
          className="p-1.5 border border-white/10 rounded hover:border-[var(--accent)] hover:text-[var(--accent)]">
          {adding ? <X size={12}/> : <Plus size={12}/>}
        </button>
      </div>

      {adding && (
        <div className="flex gap-2 mb-4" data-testid="watchlist-add-form">
          <input data-testid="watchlist-symbol-input"
            value={newSym}
            onChange={(e) => setNewSym(e.target.value.toUpperCase())}
            onKeyDown={(e) => { if (e.key === "Enter") add(); }}
            placeholder="EURUSD · BTCUSD · AAPL · SPY"
            className="input-dark flex-1 !text-sm"/>
          <button data-testid="watchlist-submit-btn" onClick={add}
            className="btn-primary text-xs px-4">Add</button>
        </div>
      )}

      {symbols.length === 0 ? (
        <div className="text-center py-6" data-testid="watchlist-empty">
          <LineIcon size={22} className="mx-auto text-[var(--text-muted)] mb-2"/>
          <p className="text-xs font-mono text-[var(--text-muted)]">
            Add forex, crypto, or stock symbols. Forge will alert you on prior-day high/low breakouts.
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {symbols.map((s) => (
            <WatchlistCard key={s} symbol={s} quote={quotes[s]} bars={sparks[s]} onRemove={() => remove(s)}/>
          ))}
        </div>
      )}
    </section>
  );
}


function WatchlistCard({ symbol, quote, bars, onRemove }) {
  const err = quote?.error;
  const today = quote?.today;
  const prior = quote?.prior;
  const source = quote?.source;
  const brokeHigh = today && prior && today.high > prior.high;
  const brokeLow  = today && prior && today.low  < prior.low;

  return (
    <div className="border border-white/10 rounded p-3 bg-black/30" data-testid={`watchlist-card-${symbol}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="font-display text-white text-base">{symbol}</span>
        {brokeHigh && (
          <span className="chip !border-[var(--profit)] !text-[var(--profit)] flex items-center gap-1" data-testid={`badge-broke-high-${symbol}`}>
            <TrendingUp size={10}/> BROKE HIGH
          </span>
        )}
        {brokeLow && (
          <span className="chip !border-[var(--loss)] !text-[var(--loss)] flex items-center gap-1" data-testid={`badge-broke-low-${symbol}`}>
            <TrendingDown size={10}/> BROKE LOW
          </span>
        )}
        <button onClick={onRemove} data-testid={`watchlist-remove-${symbol}`}
          className="ml-auto text-[var(--text-muted)] hover:text-[var(--loss)]">
          <X size={12}/>
        </button>
      </div>

      {err ? (
        <div className="text-[11px] font-mono text-[var(--text-muted)] py-3" data-testid={`watchlist-err-${symbol}`}>
          <span className="text-[var(--warning)]">·</span> {err}
          {err.includes("ALPHA_VANTAGE") && (
            <div className="mt-2">
              <Link to="/settings/notifications" className="underline hover:text-[var(--accent)]">
                Connect a broker
              </Link>{" "}
              or ask Neo to set an ALPHA_VANTAGE_API_KEY.
            </div>
          )}
        </div>
      ) : today ? (
        <>
          <div className="text-2xl font-mono text-white" data-testid={`watchlist-close-${symbol}`}>
            {today.close?.toFixed(5)}
          </div>
          <div className="flex justify-between text-[10px] font-mono mt-1 gap-3">
            <span className="text-[var(--profit)]">HOD {today.high?.toFixed(5)}</span>
            <span className="text-[var(--loss)]">LOD {today.low?.toFixed(5)}</span>
          </div>
          {prior && (
            <div className="flex justify-between text-[9px] font-mono text-[var(--text-muted)] mt-0.5 gap-3">
              <span>y·H {prior.high?.toFixed(5)}</span>
              <span>y·L {prior.low?.toFixed(5)}</span>
            </div>
          )}

          {bars && bars.length > 3 && (
            <>
              <div className="h-14 mt-2" data-testid={`watchlist-spark-${symbol}`}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={bars.map((b, i) => ({ i, close: b.close }))}>
                    <YAxis hide domain={["dataMin", "dataMax"]}/>
                    <Tooltip
                      contentStyle={{ background: "#0b0f14", border: "1px solid #1a1f26", fontSize: 10 }}
                      labelFormatter={() => ""} formatter={(v) => v?.toFixed(5)}/>
                    {prior?.high && <ReferenceLine y={prior.high} stroke="#22c55e" strokeDasharray="3 3" strokeOpacity={0.6}/>}
                    {prior?.low  && <ReferenceLine y={prior.low}  stroke="#ef4444" strokeDasharray="3 3" strokeOpacity={0.6}/>}
                    <Line type="monotone" dataKey="close" stroke="var(--accent)" strokeWidth={1.4} dot={false}/>
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="flex items-center gap-3 mt-1 text-[9px] font-mono text-[var(--text-muted)]"
                data-testid={`watchlist-spark-legend-${symbol}`}>
                <span className="flex items-center gap-1">
                  <span className="inline-block w-3 border-t-2 border-dashed" style={{ borderColor: "#22c55e" }}/>
                  prior high
                </span>
                <span className="flex items-center gap-1">
                  <span className="inline-block w-3 border-t-2 border-dashed" style={{ borderColor: "#ef4444" }}/>
                  prior low
                </span>
              </div>
            </>
          )}

          {source && (
            <div className="text-[9px] font-mono text-[var(--text-muted)] mt-1">
              src: {source}
            </div>
          )}
        </>
      ) : (
        <div className="text-[11px] font-mono text-[var(--text-muted)] py-3">Loading…</div>
      )}
    </div>
  );
}
